import {
  ArrowPathIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { PlusCircleIcon } from '@heroicons/react/24/solid';
import { CircleX } from 'lucide-react';
import React, { FormEvent, useMemo, useOptimistic, useState } from 'react';
import { toast } from 'sonner';

import { Input } from '@/components/form/Input';
import { Inputs } from '@/components/form/Inputs';
import { ToggleSwitch } from '@/components/legacy-ui';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { useModifyAccount, useMy } from '@/hooks';
import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { useUpdateSetting } from '@/hooks/useSetting';
import { useSSOId } from '@/hooks/useSSOId';
import { SettingActionCard } from '@/sections/settings/components';
import { SSOEditModal } from '@/sections/settings/components/modals/SSOEditModal';
import { SSORotateCredentialsModal } from '@/sections/settings/components/modals/SSORotateCredentialsModal';
import { RowActionsMenu } from '@/sections/settings/components/primitives/RowActionsMenu';
import { useAuth } from '@/state/auth';
import { Account } from '@/types';
import { formatRoleName, ROLE_OPTIONS } from '@/utils/role.util';

const DefaultFormValues = {
  domain: '',
  clientId: '',
  secret: '',
  issuerUrl: '',
  defaultRole: '',
  roleClaim: '',
};

interface SSOProviderSectionProps {
  email?: string;
  editable?: boolean;
}

/**
 * SSO Provider management with add/remove functionality.
 *
 * Features: Display providers, add with modal form, remove with confirmation,
 * empty state, impersonation support. Praetorian users view-only.
 *
 * Setup: TXT record (chariot={email}), domain, client ID, secret, issuer URL.
 * Docs for Okta and Azure included.
 */
export const SSOProviderSection: React.FC<SSOProviderSectionProps> = ({
  email,
  editable = true,
}) => {
  const { me, friend, isPraetorianUser, ssoDomain, logout } = useAuth();
  const currentEmail = email || friend || me;
  const isSignedInViaSSO = Boolean(ssoDomain) && ssoDomain !== 'Google';

  // SSO Providers
  const { data: accounts } = useMy(
    { resource: 'account' },
    {
      email: currentEmail,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const { mutate: link } = useModifyAccount('link', currentEmail);
  const { mutateAsync: unlink, status: unlinkStatus } = useModifyAccount(
    'unlink',
    currentEmail
  );

  const [showAddSSOModal, setShowAddSSOModal] = useState(false);
  const [ssoFormData, setSSOFormData] = useState(DefaultFormValues);
  const [confirmUnlink, setConfirmUnlink] = useState<Account | null>(null);
  const [editSSO, setEditSSO] = useState<Account | null>(null);
  const [rotateSSO, setRotateSSO] = useState<Account | null>(null);
  const enableRbac = useRbacEnabled();
  const { data: ssoIdData } = useSSOId({ email: currentEmail });
  const [showEnableSSOOnlyModal, setShowEnableSSOOnlyModal] = useState(false);

  const ssoAccounts = useMemo(
    () => accounts?.filter(account => account.member.startsWith('sso:')) || [],
    [accounts]
  );

  // SSO Login Only Setting
  const { data: settings } = useMy(
    { resource: 'setting' },
    {
      email: currentEmail,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const { mutateAsync: updateSetting } = useUpdateSetting(currentEmail);

  const serverSSOLoginOnly = useMemo(() => {
    const setting = settings?.find(s => s.name === 'enable_sso_login_only');
    if (!setting) return false;
    return (
      setting.value === 'true' ||
      (typeof setting.value === 'boolean' && setting.value)
    );
  }, [settings]);

  const [optimisticSSOLoginOnly, setOptimisticSSOLoginOnly] = useOptimistic(
    serverSSOLoginOnly,
    (_currentState, optimisticValue: boolean) => optimisticValue
  );

  const [updatingSSOLoginOnly, setUpdatingSSOLoginOnly] = useState(false);

  const handleToggleSSOLoginOnly = (checked: boolean) => {
    if (checked) {
      setShowEnableSSOOnlyModal(true);
    } else {
      void confirmToggleSSOLoginOnly(false);
    }
  };

  const confirmToggleSSOLoginOnly = async (checked: boolean) => {
    setUpdatingSSOLoginOnly(true);
    setShowEnableSSOOnlyModal(false);

    try {
      setOptimisticSSOLoginOnly(checked);

      await updateSetting({
        name: 'enable_sso_login_only',
        value: checked as unknown as string,
        silent: true,
      });

      if (checked && !isSignedInViaSSO) {
        toast.success('SSO-only login enabled. Logging out...');
        setTimeout(() => {
          void logout();
        }, 1000);
      } else {
        toast.success(
          checked
            ? 'SSO-only login enabled. Password authentication is now blocked for non-SSO accounts.'
            : 'SSO-only login disabled. Password authentication is now allowed.'
        );
      }
    } catch (error) {
      toast.error('Failed to update SSO login setting. Please try again.');
      console.error('Failed to update SSO login setting:', error);
    } finally {
      setUpdatingSSOLoginOnly(false);
    }
  };

  const handleSSOFormSubmit = (event: FormEvent) => {
    event.preventDefault();

    const domain = ssoFormData.domain.trim();
    const clientId = ssoFormData.clientId.trim();
    const secret = ssoFormData.secret.trim();
    const issuerUrl = ssoFormData.issuerUrl.trim();

    const defaultRole = ssoFormData.defaultRole.trim();
    const roleClaim = ssoFormData.roleClaim.trim();

    link({
      username: `sso:${domain}`,
      value: `sso:${domain}`,
      config: {
        name: `sso:${domain}`,
        id: clientId,
        secret,
        issuer: issuerUrl,
      },
      settings: {
        default_sso_role: enableRbac ? defaultRole : 'admin',
        role_claim: enableRbac && roleClaim ? roleClaim : undefined,
      },
    });
    setShowAddSSOModal(false);
    setSSOFormData(DefaultFormValues);
  };

  const handleUnlinkSSO = async () => {
    if (!confirmUnlink) return;
    try {
      await unlink({
        username: confirmUnlink.member,
        config: confirmUnlink.config,
        value: confirmUnlink.value,
      });
      setConfirmUnlink(null);
    } catch {
      // Modal stays open on error for user to retry or cancel
    }
  };

  return (
    <>
      {/* SSO Login Only Setting */}
      {ssoAccounts.length > 0 && (
        <SettingActionCard
          data-testid="sso-login-only-card"
          action={
            <ToggleSwitch
              id="sso-login-only"
              checked={optimisticSSOLoginOnly}
              onChange={handleToggleSSOLoginOnly}
              disabled={!editable || isPraetorianUser || updatingSSOLoginOnly}
            />
          }
        >
          <div className="flex flex-col gap-2">
            <h4 className="font-bold">Require SSO Authentication</h4>
            <span className="text-chariot-text-secondary text-xs">
              When enabled, users can only authenticate via SSO.
            </span>
          </div>
        </SettingActionCard>
      )}

      {/* SSO Cards */}
      {ssoAccounts.length === 0 ? (
        <SettingActionCard
          data-testid="sso-add-card"
          action={
            editable ? (
              <Button
                onClick={() => setShowAddSSOModal(true)}
                size="sm"
                data-testid="add-sso-provider-button"
                disabled={isPraetorianUser}
              >
                <PlusCircleIcon className="size-4" />
                Add Provider
              </Button>
            ) : undefined
          }
        >
          <div className="flex items-center gap-2">
            <h4 className="font-bold">Single Sign-On</h4>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-chariot-text-secondary text-xs">
              {isPraetorianUser
                ? 'No SSO providers configured for this organization'
                : 'No SSO providers configured'}
            </span>
          </div>
        </SettingActionCard>
      ) : (
        <>
          {ssoAccounts.map(sso => (
            <SettingActionCard
              key={sso.key}
              data-testid={`sso-provider-${sso.member}`}
              action={
                editable && !isPraetorianUser ? (
                  enableRbac ? (
                    <RowActionsMenu
                      testIdPrefix="sso-provider"
                      itemKey={sso.key}
                      actions={[
                        {
                          label: 'Edit',
                          icon: <PencilIcon className="size-4" />,
                          onClick: () => setEditSSO(sso),
                        },
                        {
                          label: 'Rotate Credentials',
                          icon: <ArrowPathIcon className="size-4" />,
                          onClick: () => setRotateSSO(sso),
                        },
                        {
                          label: 'Remove',
                          icon: <TrashIcon className="size-4" />,
                          onClick: () => setConfirmUnlink(sso),
                          variant: 'danger',
                        },
                      ]}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <Tooltip title="Rotate Credentials">
                        <button
                          onClick={() => setRotateSSO(sso)}
                          title="Rotate Credentials"
                          className="transition-opacity hover:opacity-80"
                        >
                          <ArrowPathIcon className="size-4" />
                        </button>
                      </Tooltip>
                      <Tooltip
                        title={
                          isPraetorianUser
                            ? 'Only customers can revoke SSO providers'
                            : ''
                        }
                      >
                        <button
                          onClick={() => setConfirmUnlink(sso)}
                          title="Remove SSO Provider"
                          disabled={isPraetorianUser}
                          className="transition-opacity hover:opacity-80 disabled:cursor-not-allowed"
                        >
                          <CircleX
                            className={
                              isPraetorianUser
                                ? 'stroke-chariot-text-secondary size-4'
                                : 'stroke-chariot-stroke-critical size-4'
                            }
                          />
                        </button>
                      </Tooltip>
                    </div>
                  )
                ) : undefined
              }
            >
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <h4 className="font-bold">SSO: {sso.member.split(':')[1]}</h4>
              </div>
              <span className="text-chariot-text-secondary text-xs">
                Active since {new Date(sso.updated).toLocaleDateString()}
              </span>
              {enableRbac && (
                <span className="text-chariot-text-secondary text-xs">
                  Default Role: {formatRoleName(sso.settings?.default_sso_role)}
                </span>
              )}
              {enableRbac && sso.settings?.role_claim && (
                <span className="text-chariot-text-secondary text-xs">
                  Role Claim: {sso.settings.role_claim}
                </span>
              )}
            </SettingActionCard>
          ))}
          {editable && !isPraetorianUser && (
            <SettingActionCard
              data-testid="sso-add-another-card"
              action={
                <Button
                  onClick={() => setShowAddSSOModal(true)}
                  size="sm"
                  data-testid="add-sso-provider-button"
                >
                  <PlusCircleIcon className="size-4" />
                  Add Another Provider
                </Button>
              }
            >
              <div className="flex items-center gap-2">
                <h4 className="font-bold">Single Sign-On</h4>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-chariot-text-secondary text-xs">
                  Add another SSO provider to your organization
                </span>
              </div>
            </SettingActionCard>
          )}
        </>
      )}

      {/* Add SSO Provider Modal */}
      <Modal
        open={showAddSSOModal}
        onClose={() => setShowAddSSOModal(false)}
        title="Add SSO Provider"
        size="lg"
        footer={{
          form: 'sso-setup-form',
          text: 'Integrate',
        }}
      >
        <form
          className="flex flex-col space-y-4"
          id="sso-setup-form"
          onSubmit={handleSSOFormSubmit}
        >
          <div className="bg-chariot-background-secondary rounded-lg p-4">
            <p className="mb-2 text-sm font-bold">Need help?</p>
            <div className="flex flex-col space-y-2">
              <a
                href="https://docs.praetorian.com/articles/27430843438619-okta-sso-configuration"
                target="_blank"
                rel="noopener noreferrer"
                className="text-chariot-brand-primary flex items-center space-x-2 hover:underline"
              >
                <span>How to: Okta SSO Configuration</span>
              </a>
              <a
                href="https://docs.praetorian.com/articles/27451961594139-azure-sso-configuration"
                target="_blank"
                rel="noopener noreferrer"
                className="text-chariot-brand-primary flex items-center space-x-2 hover:underline"
              >
                <span>How to: Azure SSO Configuration</span>
              </a>
            </div>
          </div>
          <div className="flex flex-col gap-1 text-sm font-bold">
            Add a TXT record to your domain with the following value:{' '}
            <code className="prose bg-chariot-background-secondary text-chariot-text-primary block overflow-x-auto rounded p-4 text-xs whitespace-pre-wrap">
              chariot={ssoIdData?.sso_id ?? '...'}
            </code>
          </div>
          <Inputs
            inputs={[
              {
                label: 'Domain',
                value: ssoFormData.domain,
                placeholder: 'acme.com',
                name: 'domain',
                required: true,
              },
              {
                label: 'Client ID',
                value: ssoFormData.clientId,
                placeholder: '1a2b3c4d-5e6f-7g8h-9i0j',
                name: 'clientId',
                required: true,
              },
              {
                label: 'Secret',
                value: ssoFormData.secret,
                type: Input.Type.PASSWORD,
                placeholder: '**********',
                name: 'secret',
                required: true,
              },
              {
                label: 'Issuer URL',
                value: ssoFormData.issuerUrl,
                placeholder:
                  'https://login.microsoftonline.com/1a2b3c4d-5e6f-7g8h-9i0j/v2.0',
                name: 'issuerUrl',
                required: true,
              },
              {
                label: 'Default Role for SSO Users',
                value: ssoFormData.defaultRole,
                placeholder: 'Select a default role',
                name: 'defaultRole',
                type: Input.Type.SELECT,
                options: ROLE_OPTIONS.map(opt => ({
                  value: opt.value,
                  label: opt.label,
                })),
                required: true,
                hidden: !enableRbac,
              },
              {
                label: 'Role Claim Name (Optional)',
                value: ssoFormData.roleClaim,
                placeholder: 'e.g. app_role',
                name: 'roleClaim',
                hidden: !enableRbac,
              },
            ]}
            onChange={values =>
              setSSOFormData(formData => ({ ...formData, ...values }))
            }
          />
        </form>
      </Modal>

      {/* Enable SSO-Only Login Confirmation Modal */}
      <Modal
        title="Enable SSO-Only Login?"
        open={showEnableSSOOnlyModal}
        onClose={
          updatingSSOLoginOnly
            ? () => {}
            : () => setShowEnableSSOOnlyModal(false)
        }
        footer={{
          text: updatingSSOLoginOnly
            ? 'Enabling...'
            : !isSignedInViaSSO
              ? 'Enable and Log Out'
              : 'Enable SSO-Only Login',
          className: 'min-w-40',
          disabled: updatingSSOLoginOnly,
          onClick: () => confirmToggleSSOLoginOnly(true),
        }}
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
            <p className="mb-2 font-semibold text-yellow-800 dark:text-yellow-200">
              ⚠️ Warning: This will restrict user authentication
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Users without SSO access will not be allowed to access this
              organization.
            </p>
          </div>

          {!isSignedInViaSSO && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <p className="mb-2 font-semibold text-red-800 dark:text-red-200">
                🚪 You will be logged out immediately
              </p>
              <p className="text-sm text-red-700 dark:text-red-300">
                Since you are not currently signed in via SSO, you will be
                logged out after enabling this setting.
              </p>
            </div>
          )}

          <p className="text-chariot-text-secondary text-sm">
            Are you sure you want to enable SSO-only authentication?
          </p>
        </div>
      </Modal>

      {/* SSO Provider Edit Modal */}
      <SSOEditModal
        ssoAccount={editSSO}
        onClose={() => setEditSSO(null)}
        email={currentEmail}
      />

      {/* SSO Credential Rotation Modal */}
      <SSORotateCredentialsModal
        ssoAccount={rotateSSO}
        onClose={() => setRotateSSO(null)}
        email={currentEmail}
      />

      {/* SSO Provider Removal Confirmation Modal */}
      <Modal
        title="Remove SSO Provider"
        open={!!confirmUnlink}
        onClose={
          unlinkStatus === 'pending' ? () => {} : () => setConfirmUnlink(null)
        }
        footer={
          confirmUnlink
            ? {
                text:
                  unlinkStatus === 'pending'
                    ? 'Removing...'
                    : 'Remove Provider',
                className: 'min-w-32',
                disabled: unlinkStatus === 'pending',
                onClick: handleUnlinkSSO,
              }
            : undefined
        }
      >
        {confirmUnlink && (
          <div className="space-y-4">
            <p>
              Are you sure you want to remove the SSO provider for{' '}
              <strong>{confirmUnlink.member.split(':')[1]}</strong>?
            </p>
            <p className="text-chariot-text-secondary text-sm">
              Users authenticating through this provider will no longer be able
              to access your organization.
            </p>
          </div>
        )}
      </Modal>
    </>
  );
};
